import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Flag } from "./primitives.jsx";

/**
 * Što biraču treba od firme, i ništa više.
 *
 * Namerno uže od `Company` iz administracije: isti birač koristi i otkup, kome firme stižu sa
 * `/procurement/suppliers` i nose samo šifru i naziv. `Company` ovaj oblik zadovoljava, pa stari
 * pozivaoci ostaju netaknuti.
 */
export type CompanyOption = {
  id: string;
  name: string;
  country?: string | null;
  vatNumber?: string | null;
};

type Props = {
  companies: CompanyOption[];
  value: string | null;
  onChange: (id: string | null) => void;
  placeholder?: string;
  disabled?: boolean;
  /**
   * Kad je zadato, pretragu radi pozivalac (na serveru), a ovaj birač više ne filtrira sam.
   *
   * Bez toga birač filtrira spisak koji mu je dat. Nad 2.113 dobavljača taj spisak putuje do pregledača
   * pri svakom otvaranju ekrana i seče se ovde — pa ko traži firmu kuca u nešto što je već odsečeno.
   */
  onQueryChange?: (q: string) => void;
  /** Koliko pogodaka ukupno postoji, kad pretragu radi pozivalac. Služi poruci o odsecanju. */
  totalMatches?: number;
};

/**
 * Searchable, app-styled company picker (same pattern as tm/ProjectCombobox).
 * Filters on name, country and VAT number; shows the country flag like the
 * orders table does. Use this instead of a native <select> for companies.
 */
export function CompanyCombobox({
  companies, value, onChange, placeholder, disabled, onQueryChange, totalMatches,
}: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selected = useMemo(() => companies.find((c) => c.id === value) ?? null, [companies, value]);

  const LIMIT = 50;

  /**
   * Pogodci, i koliko ih je ukupno.
   *
   * Spisak se seče na pedeset, jer padajući spisak od dve hiljade redova nije izbor nego posao. Ali
   * odsečeno se **mora videti**: bez toga birač nad 2.113 dobavljača izgleda kao da firmi ima pedeset,
   * i čovek odustane umesto da ukuca ime. Isto pravilo koje već važi za zbir nad odsečenim spiskom na
   * kontrolnoj tabli — broj koji ne kaže da je delimičan je gori od nijednog.
   */
  const { filtered, ukupnoPogodaka } = useMemo(() => {
    // Kad pretragu radi pozivalac, `companies` su već pogoci sa servera i ovde se ne filtrira ponovo —
    // inače bi se isti tekst primenio dvaput, a server je već odsekao.
    if (onQueryChange) {
      return { filtered: companies, ukupnoPogodaka: totalMatches ?? companies.length };
    }
    const q = query.trim().toLowerCase();
    const svi = !q
      ? companies
      : companies.filter((c) =>
          c.name.toLowerCase().includes(q)
          || (c.country ?? "").toLowerCase().includes(q)
          || (c.vatNumber ?? "").toLowerCase().includes(q));
    return { filtered: svi.slice(0, LIMIT), ukupnoPogodaka: svi.length };
  }, [companies, query, onQueryChange, totalMatches]);

  useEffect(() => {
    function close(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    // 'click' rather than 'mousedown' — pick runs in the option's click
    // event, and a mousedown listener would tear down the dropdown before
    // the selection commits.
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, []);

  useEffect(() => { setActiveIdx(0); }, [query, open]);

  function pick(c: CompanyOption) { onChange(c.id); setQuery(""); setOpen(false); }
  function clear() { onChange(null); setQuery(""); inputRef.current?.focus(); }

  function onKey(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowDown") { e.preventDefault(); setOpen(true); setActiveIdx((i) => Math.min(i + 1, filtered.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter" && open && filtered[activeIdx]) { e.preventDefault(); pick(filtered[activeIdx]); }
    else if (e.key === "Escape") setOpen(false);
  }

  const ph = placeholder ?? t("orders.import.selectCompany");

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <div
        style={{
          display: "flex", alignItems: "center", gap: 6,
          padding: "6px 8px", border: "1px solid var(--border)", borderRadius: 6,
          background: disabled ? "var(--surface-2)" : "var(--surface)",
          cursor: disabled ? "not-allowed" : "text",
          opacity: disabled ? 0.8 : 1,
        }}
      >
        {selected && !open ? (
          <span style={{ flex: 1, fontSize: 14, display: "flex", alignItems: "center", gap: 8 }}>
            {selected.country && <Flag code={selected.country} />}
            <b>{selected.name}</b>
            {selected.country && <span style={{ color: "var(--ink-3)" }}>{selected.country}</span>}
          </span>
        ) : (
          <input
            ref={inputRef}
            type="text"
            value={query}
            disabled={disabled}
            onFocus={() => !disabled && setOpen(true)}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
              onQueryChange?.(e.target.value);
            }}
            onKeyDown={onKey}
            placeholder={selected ? selected.name : ph}
            style={{ flex: 1, border: "none", outline: "none", fontSize: 14, fontFamily: "inherit", background: "transparent" }}
            aria-autocomplete="list" aria-expanded={open} role="combobox"
          />
        )}
        {selected && !disabled && (
          <button type="button" onClick={(e) => { e.stopPropagation(); clear(); }}
                  aria-label={t("action.clear", "Clear")}
                  style={{ background: "transparent", border: "none", color: "var(--ink-3)", fontSize: 16, cursor: "pointer", padding: 2 }}>
            ×
          </button>
        )}
        <span style={{ color: "var(--ink-4)", fontSize: 12 }} aria-hidden>▾</span>
      </div>
      {open && (
        <div role="listbox" className="dd-scroll" style={{
          position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0,
          maxHeight: 280, overflowY: "auto", background: "var(--surface)",
          border: "1px solid var(--border)", borderRadius: 6,
          boxShadow: "var(--shadow-2)", zIndex: 20,
        }}>
          {ukupnoPogodaka > filtered.length && (
            <div
              style={{
                padding: "6px 10px", fontSize: 11, color: "var(--ink-3)",
                borderBottom: "1px solid var(--border)", position: "sticky", top: 0,
                background: "var(--surface)",
              }}
            >
              {t("company.pick.cut", "Prikazano {{prikazano}} od {{svih}} — ukucajte ime da suzite.", {
                prikazano: filtered.length,
                svih: ukupnoPogodaka,
              })}
            </div>
          )}
          {filtered.length === 0 ? (
            <div style={{ padding: 10, fontSize: 13, color: "var(--ink-3)" }}>
              {t("common.empty", "No matches.")}
            </div>
          ) : filtered.map((c, i) => (
            <div
              key={c.id} role="option" aria-selected={c.id === value}
              onMouseEnter={() => setActiveIdx(i)}
              // mousedown + preventDefault: commit before any blur/click race
              // tears the dropdown down or refocuses the wrapper.
              onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); pick(c); }}
              style={{
                padding: "8px 10px", cursor: "pointer", fontSize: 14,
                display: "flex", alignItems: "center", gap: 8,
                background: i === activeIdx ? "var(--surface-2)" : "transparent",
              }}>
              {c.country && <Flag code={c.country} />}
              <span style={{ fontWeight: 600 }}>{c.name}</span>
              {c.country && <span style={{ color: "var(--ink-3)", fontSize: 12 }}>{c.country}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
